<template>
  <Pane :value="computedValue"
        :tabs="tabs"
        current-tab="share"
        temporary
        data-locator="signed-documents-share-pane"
        @closeButtonClicked="computedValue = false"
  >
    <template #content-share>
      <v-row class="px-5 pt-5">
        <v-col>
          <h4 class="text-text-primary">{{ $t('project.signed-documents.pane.SharePane.selectedDocuments', documents.length) }}</h4>
        </v-col>
      </v-row>
      <v-row class="px-5 mb-3">
        <v-col>
          <v-chip v-for="document in documents"
                  :key="`document-${document.type}-${document.id}`"
                  class="text-text-secondary mr-2 mb-2"
                  variant="outlined"
                  closable
                  @click:close="$emit('removeDocumentToShare', document)"
          >
            <app-material-icon class="DocumentIcon mr-1"
                               name="task"
            />
            <AppTooltip>
              <template #activator="{ props }">
                <div class="Document-name" v-bind="props">
                  {{document.basename || document.name}}
                </div>
              </template>
              <span>{{document.basename || document.name}}</span>
            </AppTooltip>
          </v-chip>
        </v-col>
      </v-row>
      <div class="px-5 mb-3">
        <v-row>
          <v-col>
            <DocumentSharePicker v-if="computedValue"
                                 users-only
                                 :force-pm-selection="false"
                                 :documents="documents"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-divider />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-checkbox v-model="sendNotif"
                        hide-details
                        class="mt-0 pt-0"
                        :label="$t('project.signed-documents.pane.SharePane.sendNotif')"
                        data-locator="notification-checkbox"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <VisualTextEditor
              v-model:value="notifyMessage"
              ref="notifyMessageEditor"
              counter="footer"
              :editing="true"
              :error-messages="notifyMessageErrors"
              :max-length="notifyMessageMaxLength"
              :padding="true"
              :placeholder="$t('project.signed-documents.pane.SharePane.optionalMessageWithCopy')"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <div class="d-flex justify-end ga-4">
              <app-button
                type="outlined"
                variant="neutral"
                data-locator="cancel-button"
                @click="computedValue = false"
              >
                {{$t('common.cancel')}}
              </app-button>
              <app-button
                :loading="shareLoading"
                :disabled="shareDisabled"
                data-locator="confirm-button"
                @click="share"
              >
                {{ $t("common.share") }}
              </app-button>
            </div>
          </v-col>
        </v-row>
      </div>
    </template>
  </Pane>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import Pane from '@/common/Pane.vue'
import { escapeHtml } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import { GET_SIGNED_DOCUMENTS } from '@/store/modules/signed-documents/action_types'

const NOTIFY_MESSAGE_MAX_LENGTH = 400

export default {
  name: 'SharePane',
  components: {
    DocumentSharePicker,
    Pane,
    VisualTextEditor,
    AppTooltip,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    documents: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      tabs: [
        {
          title: this.$t('common.share'),
          id: 'share',
        },
      ],
      sendNotif: true,
      notifyMessage: '',
      notifyMessageMaxLength: NOTIFY_MESSAGE_MAX_LENGTH,
      shareLoading: false,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapState('documentSharePicker', ['selectedMembers']),
    computedValue: {
      get () { return this.modelValue },
      set (value) {
        if (!value) {
          this.$emit('update:documents', [])
          this.resetShareForm()
        }

        this.$emit('update:model-value', value)
      },
    },
    notifyMessageLength () {
      return escapeHtml(this.notifyMessage).length
    },
    notifyMessageIsTooLong () {
      return this.notifyMessageLength > this.notifyMessageMaxLength
    },
    notifyMessageErrors () {
      const errors = []

      if (this.notifyMessageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    shareDisabled () {
      return this.documents.length === 0 || this.notifyMessageIsTooLong
    },
  },
  methods: {
    ...mapActions('signedDocuments', ['shareOriginal', GET_SIGNED_DOCUMENTS]),
    resetShareForm () {
      this.sendNotif = true
      this.notifyMessage = ''
      this.shareLoading = false
      this.$refs.notifyMessageEditor?.clear()
    },
    async share () {
      await this.shareOriginal({
        mnemo: this.roomMnemo,
        data: {
          envelopesIds: this.documents.map(o => o.id),
          withUsers: this.selectedMembers.map(o => o.id),
          notifyMessage: this.notifyMessage,
        },
      })
        .then(() => {
          this.computedValue = false
          this.GET_SIGNED_DOCUMENTS({ mnemo: this.$route.params.mnemo, params: {
            rightsDetails: true,
            fileDetails: true,
            signatureDetails: true,
          }})
          toast.success(this.$t('project.signed-documents.pane.SharePane.shareSuccess', this.documents.length))
        })
    },
  },
}
</script>

<style scoped lang="scss">
  .Document-name {
    max-width: 350px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .Document-chip {
  .v-chip__close:hover {
    color: rgb(var(--v-theme-error)) !important;
  }
  }
</style>
